const SITE_ID = window.ssSiteId;
const BLOG_SITE_ID = 'm6y9h3';
const PAGE_LOAD_ID = window.ssPageLoadId;

const { objectToUrlParams, getCookie, sendPreflight, filterProductsByEarlyDrop } = window.ssUtils;

// Global autocomplete parameters that can be modified from anywhere
window.ssAutocompleteParams = {
    siteId: SITE_ID,
    resultsFormat: 'json',
    q: '',
    userId: getCookie('ssUserId') || '',
    sessionId: getCookie('ssSessionIdNamespace') || '',
    pageLoadId: PAGE_LOAD_ID,
    resultsPerPage: 12,
    domain: window.location.href
};

/**
 * Fetches autocomplete results based on query data
 * @param {Object} queryData - Query parameters to send to the API
 * @returns {Promise<Object>} - The autocomplete results
 */
async function queryAutocomplete(queryData = {}) {
    try {
        // Update global params with the provided queryData
        Object.assign(window.ssAutocompleteParams, queryData);

        const autocompleteParams = objectToUrlParams(window.ssAutocompleteParams);
        const autocompleteUrl = `https://${SITE_ID}.a.searchspring.io/api/search/autocomplete.json?bgfilter.status=active&${autocompleteParams}`;
        const blogAutocompleteParams = {
            ...window.ssAutocompleteParams,
            siteId: BLOG_SITE_ID
        }
        const queryBlogAutocompleteParams = objectToUrlParams(blogAutocompleteParams);
        const blogAutocompleteUrl = `https://${BLOG_SITE_ID}.a.searchspring.io/api/search/autocomplete.json?${queryBlogAutocompleteParams}`;

        let preflightData;
        let response;
        let contentResponse;

        if (window.ssAutocompleteParams['filter.blog_posts']) {

            // Send preflight and fetch autocomplete results in parallel
            [preflightData, response] = await Promise.all([
                sendPreflight(),
                fetch(blogAutocompleteUrl, {
                    method: 'GET',
                    headers: { accept: 'application/json' }
                })
            ]);
        } else {
            // Send preflight and fetch autocomplete results in parallel
            [preflightData, response, contentResponse] = await Promise.all([
                sendPreflight(),
                fetch(autocompleteUrl, {
                    method: 'GET',
                    headers: { accept: 'application/json' }
                }),
                fetch(blogAutocompleteUrl, {
                    method: 'GET',
                    headers: { accept: 'application/json' }
                })
            ]);
        }



        if (!response.ok) {
            throw new Error(`API responded with status: ${response.status}`);
        }

        const data = await response.json();
        const filteredResults = await filterProductsByEarlyDrop(data.results);
        const blogData = contentResponse ? await contentResponse.json() : null;

        let results;
        if (blogData?.results && blogData?.results?.length > 0) {
            const firstThree = filteredResults.slice(0, 3);
            const blogFirst = blogData.results[0];
            const rest = filteredResults.slice(3);
            results = [...firstThree, blogFirst, ...rest];
        } else {
            results = [...filteredResults];
        }

        // Process and render products
        const productCards = new ProductCards('product-list');
        if (results?.length > 0) {
            productCards.render(results, true);
        } else {
            productCards.noResults();
        }

        return data;
    } catch (error) {
        console.error('Error in queryAutocomplete:', error);
        return null;
    }
}

// Helper functions exposed to window for external access
window.queryAutocomplete = queryAutocomplete;

window.updateAutocompleteParams = function(newParams) {
    Object.assign(window.ssAutocompleteParams, newParams);
    return window.ssAutocompleteParams;
};

window.removeAutocompleteParam = function(param) {
    if (window.ssAutocompleteParams.hasOwnProperty(param)) {
        delete window.ssAutocompleteParams[param];
        return true;
    }
    return false;
};

/**
 * Fetches search suggestions based on query
 * @param {string} query - The search query
 * @returns {Promise<Object>} - The suggestion results
 */
async function querySuggestions(query) {
    try {
        const suggestionParams = objectToUrlParams({
            siteId: SITE_ID,
            q: query
        });

        const suggestUrl = `https://${SITE_ID}.a.searchspring.io/api/suggest/query?bgfilter.status=active&${suggestionParams}`;
        const response = await fetch(suggestUrl, {
            method: 'GET',
            headers: { accept: 'application/json' }
        });

        if (!response.ok) {
            throw new Error(`API responded with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error in querySuggestions:', error);
        return null;
    }
}

window.querySuggestions = querySuggestions;

/**
 * Fetches trending search terms
 * @returns {Promise<Object>} - Trending search data
 */
async function queryTrending() {
    try {
        const suggestionParams = objectToUrlParams({
            siteId: SITE_ID,
            limit: 6
        });

        const suggestUrl = `https://${SITE_ID}.a.searchspring.io/api/suggest/trending?bgfilter.status=active&${suggestionParams}`;
        const response = await fetch(suggestUrl, {
            method: 'GET',
            headers: { accept: 'application/json' }
        });

        if (!response.ok) {
            throw new Error(`API responded with status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.trending?.queries?.length > 0) {
            const suggestions = data.trending.queries.map(query => ({
                name: query.searchQuery
            }));

            const trendingSearches = new SearchSuggestion('search-suggestion-list');
            trendingSearches.render(suggestions, true);
        }

        return data;
    } catch (error) {
        console.error('Error in queryTrending:', error);
        return null;
    }
}

document.addEventListener('DOMContentLoaded', function() {
    const searchInput = document.getElementById('Search-In-Modal');
    if (!searchInput) return;

    // Use a debounce function to limit API calls during typing
    const debounce = (fn, delay) => {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn.apply(this, args), delay);
        };
    };

    // Try to load trending searches when page loads
    queryTrending().catch(error => {
        console.error('Error fetching trending searches:', error);
    });

    // Handle search input with debounced event handler
    const debouncedFetch = debounce(fetchAutocompleteResults, 175);
    searchInput.addEventListener('input', function() {
        const query = this.value.trim();
        debouncedFetch(query);
    });

    /**
     * Fetches autocomplete results and suggestions
     * @param {string} query - The search query
     */
    async function fetchAutocompleteResults(query) {
        if (!query) {
            // Hide autocomplete container when query is empty
            const autocompleteContainer = document.getElementById('ss-autocomplete-container');
            if (autocompleteContainer) {
                autocompleteContainer.classList.remove('open');
            }
            return;
        }

        try {
            // Run both requests in parallel for better performance
            const [suggestData, autocompleteData] = await Promise.all([
                querySuggestions(query),
                queryAutocomplete({q: query})
            ]);

            const autocompleteContainer = document.getElementById('ss-autocomplete-container');
            if (autocompleteContainer) {
                if (suggestData || autocompleteData) {
                    autocompleteContainer.classList.add('open');
                } else {
                    autocompleteContainer.classList.remove('open');
                }
            }

            if (suggestData) {
                renderSuggestions(suggestData, query);
            }

            if (autocompleteData?.facets?.length > 0) {
                renderFacets(autocompleteData.facets, query);
            }
        } catch (error) {
            console.error('Error in fetchAutocompleteResults:', error);
        }
    }

    /**
     * Renders suggestions in the UI
     * @param {Object} suggestData - The suggestion data from the API
     * @param {string} query - The search query
     */
    function renderSuggestions(suggestData, query) {
        const searchedSuggestions = new SearchSuggestion('search-suggestion-list');

        // Collect all valid suggestions
        const suggestions = [
            {name: query}, // Searched value
            ...(suggestData?.suggested?.text ? [{name: suggestData.suggested.text}] : []), // Suggested value
            ...(suggestData?.alternatives || []).map(suggestion => ({name: suggestion.text})) // Alternatives
        ].filter(item => !!item.name); // Filter out any undefined or empty values

        searchedSuggestions.render(suggestions);
    }

    /**
     * Renders facets in the UI
     * @param {Array} facets - The facets from the API
     * @param {string} query - The search query
     */
    function renderFacets(facets, query) {
        const filterList = document.getElementById('filter-list');
        if (!filterList) return;

        filterList.innerHTML = '';

        // Render Size Category filter
        const tagFacet = facets.find(facet => facet.field === "tags");
        if (tagFacet) {
            const values = tagFacet.values.filter(value =>
                value.value === 'adult' || value.value === 'youth'
            );

            if (values.length > 0) {
                const tagFilter = new FilterType('filter-list');
                tagFilter.render({
                    query,
                    name: 'Size Category',
                    field: 'tags',
                    values
                });
            }
        }

        // Render Product Type filter
        const productTypeFacet = facets.find(facet => facet.field === "product_type");
        if (productTypeFacet && productTypeFacet.values.length > 0) {
            const productTypeFilter = new FilterType('filter-list');
            productTypeFilter.render({
                query,
                name: 'Product Type',
                field: 'product_type',
                values: productTypeFacet.values
            });
        }

        const blogFilter = new FilterType('filter-list');
        blogFilter.render({
            query,
            field: 'blog_posts',
            values: [{label: 'Blog Posts', value: 'blog_posts'}]
        });

    }
});